import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Descriptions, Input, Modal, Space, Table, Tag, Typography } from "antd"
import { useNavigate, useParams } from "react-router-dom"
import { notifications } from "notifications"
import { ClusterMember, MatchClusterStatus, MatchDecision, PairwiseVerdict } from "../store/types"

const STATUS_COLOR: Record<MatchClusterStatus, string> = {
  FORMING: "blue",
  VALIDATING: "gold",
  VALIDATED: "green",
  ABANDONED: "default",
  REJECTED: "red",
}

const DECISION_COLOR: Record<MatchDecision, string> = {
  SAME: "green",
  DIFFERENT: "red",
  UNSURE: "gold",
}

const MIN_REASON_LENGTH = 8

export const ClusterDetail: FC = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { dataStore: { matchClusteringStore } } = useStore()

  const [rejectOpen, setRejectOpen] = useState(false)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (id) matchClusteringStore.loadClusterDetail(id)
  }, [id, matchClusteringStore])

  const cluster = matchClusteringStore.selectedCluster

  if (!cluster || cluster.id !== id) {
    return <Card loading title="Loading cluster…" />
  }

  const canReject = cluster.status === "FORMING" || cluster.status === "VALIDATING"

  const onReject = async() => {
    if (!id) return
    if (reason.trim().length < MIN_REASON_LENGTH) {
      notifications.error(`Reason must be at least ${MIN_REASON_LENGTH} characters`)

      return
    }
    setSubmitting(true)
    try {
      const res = await matchClusteringStore.rejectCluster(id, reason.trim())
      if (res?.outcome === "rejected") {
        notifications.success("Cluster rejected")
        setRejectOpen(false)
        setReason("")
      } else if (res?.outcome === "conflict") {
        notifications.error(`Cluster is in status ${res.currentStatus} and cannot be rejected`)
      } else if (res?.outcome === "not_found") {
        notifications.error("Cluster not found")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const memberColumns = [
    { title: "Recording", dataIndex: "recordingId", key: "recordingId" },
    { title: "Account", dataIndex: "accountId", key: "accountId" },
    {
      title: "Recording ended",
      dataIndex: "recordingEndedAt",
      key: "recordingEndedAt",
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "Joined",
      dataIndex: "joinedAt",
      key: "joinedAt",
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ]

  const verdictColumns = [
    { title: "Recording A", dataIndex: "recordingIdA", key: "recordingIdA" },
    { title: "Recording B", dataIndex: "recordingIdB", key: "recordingIdB" },
    {
      title: "Decision",
      dataIndex: "decision",
      key: "decision",
      width: 110,
      render: (d: MatchDecision) => <Tag color={DECISION_COLOR[d]}>{d}</Tag>,
    },
    {
      title: "Reasons",
      dataIndex: "reasons",
      key: "reasons",
      render: (rs: string[]) => (rs.length ? <Typography.Text>{rs.join(", ")}</Typography.Text> : "—"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Card
        title={<Space><span>Cluster {cluster.id}</span><Tag color={STATUS_COLOR[cluster.status]}>{cluster.status}</Tag></Space>}
        extra={
          <Space>
            <Button onClick={() => navigate("/match-clustering/clusters")}>Back</Button>
            <Button danger disabled={!canReject} onClick={() => setRejectOpen(true)}>Reject</Button>
          </Space>
        }
      >
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Game ID">{cluster.gameId}</Descriptions.Item>
          <Descriptions.Item label="Members">{cluster.memberCount}</Descriptions.Item>
          <Descriptions.Item label="Freeze deadline">{new Date(cluster.freezeDeadline).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Created">{new Date(cluster.createdAt).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Updated">{new Date(cluster.updatedAt).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Validated">
            {cluster.validatedAt ? new Date(cluster.validatedAt).toLocaleString() : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Abandoned">
            {cluster.abandonedAt ? new Date(cluster.abandonedAt).toLocaleString() : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Rejected by">{cluster.rejectedBy ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="Rejected at">
            {cluster.rejectedAt ? new Date(cluster.rejectedAt).toLocaleString() : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Rejection reason" span={2}>{cluster.rejectionReason ?? "—"}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={`Members (${cluster.members.length})`}>
        <Table<ClusterMember>
          rowKey="recordingId"
          dataSource={cluster.members}
          columns={memberColumns}
          pagination={false}
        />
      </Card>

      <Card title={`Pairwise verdicts (${cluster.pairwiseVerdicts.length})`}>
        <Table<PairwiseVerdict>
          rowKey="id"
          dataSource={cluster.pairwiseVerdicts}
          columns={verdictColumns}
          pagination={false}
        />
      </Card>

      <Modal
        title="Reject cluster"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={onReject}
        okText="Reject"
        okButtonProps={{ danger: true, loading: submitting }}
      >
        <p>This will mark the cluster as REJECTED and prevent any cluster.validated event from being published.</p>
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={`Reason (minimum ${MIN_REASON_LENGTH} characters) — visible to auditors`}
        />
      </Modal>
    </Space>
  )
})
